import { type CompiledSkeleton, type ConstraintSolver, TRANSFORM_STRIDE } from "./types.js";

export const MATRIX_STRIDE = 6;

export function applyRotationLimits(definition: CompiledSkeleton, local: Float64Array): number[] {
  const clampedBones: number[] = [];
  for (let bone = 0; bone < definition.bones.length; bone += 1) {
    const limit = definition.bones[bone].rotationLimit;
    if (!limit) continue;
    const offset = bone * TRANSFORM_STRIDE;
    const angle = Math.atan2(local[offset + 3], local[offset + 2]);
    const clamped = Math.max(limit.min, Math.min(limit.max, angle));
    if (Math.abs(clamped - angle) <= 1e-12) continue;
    local[offset + 2] = Math.cos(clamped);
    local[offset + 3] = Math.sin(clamped);
    clampedBones.push(bone);
  }
  return clampedBones;
}

export function evaluateWorldTransforms(
  definition: CompiledSkeleton,
  local: Float64Array,
  constraints: readonly ConstraintSolver[] = [],
  out = new Float64Array(definition.bones.length * MATRIX_STRIDE),
): Float64Array {
  for (let bone = 0; bone < definition.bones.length; bone += 1) {
    const localOffset = bone * TRANSFORM_STRIDE;
    const matrixOffset = bone * MATRIX_STRIDE;
    const cos = local[localOffset + 2];
    const sin = local[localOffset + 3];
    const a = cos * local[localOffset + 4];
    const b = sin * local[localOffset + 4];
    const c = -sin * local[localOffset + 5];
    const d = cos * local[localOffset + 5];
    const tx = local[localOffset];
    const ty = local[localOffset + 1];
    const parent = definition.bones[bone].parent;
    if (parent < 0) {
      out.set([a, b, c, d, tx, ty], matrixOffset);
      continue;
    }
    const parentOffset = parent * MATRIX_STRIDE;
    const pa = out[parentOffset];
    const pb = out[parentOffset + 1];
    const pc = out[parentOffset + 2];
    const pd = out[parentOffset + 3];
    const ptx = out[parentOffset + 4];
    const pty = out[parentOffset + 5];
    out[matrixOffset] = pa * a + pc * b;
    out[matrixOffset + 1] = pb * a + pd * b;
    out[matrixOffset + 2] = pa * c + pc * d;
    out[matrixOffset + 3] = pb * c + pd * d;
    out[matrixOffset + 4] = pa * tx + pc * ty + ptx;
    out[matrixOffset + 5] = pb * tx + pd * ty + pty;
  }
  for (const constraint of constraints) constraint({ definition, local, world: out });
  return out;
}
